import {
  Button, Checkbox, Box, Container, Paper, InputAdornment, IconButton, Grid,
  TextField, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, CircularProgress, Select, MenuItem, FormControl, InputLabel, TablePagination,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import exportFromJSON from 'export-from-json';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuthContext';
import { getCompanyList } from 'api/company';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import { useSnackbar } from 'components/snackbar';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/**
 * Company List page
 * @returns {React.ReactElement} The Company List page
 */

const CompanyList = () => {
  const navigate = useNavigate();
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [selectAll, setSelectAll] = useState(false);
  const { user } = useAuth();
  const [searchParameter, setSearchParameter] = useState(false);
  const [industryList, setIndustryList] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const company_types = [
    "Micro, Small, and Medium Enterprises (MSMEs)",
    "Private Limited Company (Pvt Ltd)",
    "Public Limited Company (Ltd)",
    "Startup Company",
    "Holding Company and Subsidiary Company",
    "Joint Venture Company",
    "Government Company",
    "Foreign Company",
    "One Person Company (OPC)",
    "Limited Liability Partnership (LLP)",
    "Section 8 Company",
    "Producer Company",
  ];

  const defaultValues = {
    companyName: "",
    industrySector: "",
    types: "",
    size: "",
    employees: "",
    location: [],
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { control, handleSubmit, reset, formState: { isSubmitting }, } = methods;

  useEffect(() => {
    fetchData();
    fetchIndustryList();
  }, [getCompanyList]);

  /**
   * Fetches the company list and formats the data
   * @returns {Promise<void>}
   * @listens setCompanyList
   * @listens setError
   * @listens setLoading
   * @listens console.error
   */
  const fetchData = async () => {
    try {
      const response = await getCompanyList();
      if (response?.success && response.data != null) {
        setCompanyList(response.data);
      } else {
        console.error(`Failed to fetch company list. Status: ${response.status}`);
        setError(`Failed to fetch company list. Error fetching data: ${error.message}`);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(`Error fetching data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles adding a new company
   * Navigates to the company profile page
   * Passes the current path as a state parameter to the company profile page
   * @returns {void}
   */
  const handleAddCompany = () => {
    navigate("/company-profile", { state: { prevPath: location.pathname } });
  };

  /**
   * Handles navigation to the specified path.
   * If the user is not logged in, it navigates to the login page.
   * @param {string} path - The path to navigate to.
   * @returns {undefined}
   */
  const handleNavigation = (path) => {
    if (user) {
      navigate(path);
    } else {
      navigate('/login');
    }
  };

  /**
   * Toggles the search parameter and resets the search form
   * when the search parameter is true.
   * @returns {undefined}
   */
  const handleSearch = () => {
    setSearchParameter(prevState => !prevState);
    if (searchParameter) {
      setLoading(true);
      reset(defaultValues);
      fetchData();
    }
  };

  /**
   * Downloads the selected companies data as a CSV file.
   * The data excludes the user_id column and renames some of the columns.
   * The columns that are included are:
   * - Company Id
   * - Date of company creation
   * - Company Name
   * - CIN
   * - Industry
   * - Website
   * - Overview
   * - Phone Number
   * - Email id
   * @returns {undefined}
   */
  const downloadSelectedCompaniesData = () => {

    const selectedCompaniesData = companyList?.filter(company => selectedCompanies?.includes(company?.id));

    const excludedColumns = ['user_id'];
    const modifiedCompanyList = selectedCompaniesData?.map(company => {
      const modifiedCompany = { ...company };
      excludedColumns.forEach(column => delete modifiedCompany[column]);
      return modifiedCompany;
    });

    const renamedCompanyList = modifiedCompanyList.map(company => ({
      'Company Id': company['id'] || 'N/A',
      'Date of company creation': company['created_at'] ? dayjs(company['created_at']).format('DD MMM, YYYY') : 'N/A',
      'Company Name': company['fullName'] || 'N/A',
      'CIN': company['identification_number'] || 'N/A',
      'Industry': company['industrySector'],
      'Website': company['website'] || 'N/A',
      'Overview': company['description'] ? company['description'].replace(/<[^>]*>/g, '') : 'N/A',
      'Phone Number': company['PhoneNumber'] || 'N/A',
      'Email id': company['email'] || 'N/A',
      // "Data of Registration": company['date_of_registration'] ? dayjs(company['date_of_registration']).format('DD MMM, YYYY') : 'N/A',
    }));

    const fileName = `companyList_${Date.now()}`;
    const exportType = exportFromJSON.types.csv
    exportFromJSON({ data: renamedCompanyList, fileName, exportType });
  }

  /**
   * Handles the click event on a row in the DataGrid component.
   * Navigates to the company details page with the userid as a parameter.
   * @param {string} userid Unique identifier of the company whose row was clicked.
   * @returns {undefined}
   */
  const handleRowClick = (userid) => {
    handleNavigation(`/company-details/${userid}`);
  };

  /**
   * Handles the checkbox change event for a company.
   * If the checkbox is checked, it adds the company's id to the selectedCompanies state.
   * If the checkbox is unchecked, it removes the company's id from the selectedCompanies state.
   * @param {object} event - The checkbox change event.
   * @param {string} companyId - The id of the company.
   * @returns {undefined}
   */
  const handleCheckboxChange = (event, companyId) => {
    if (event.target.checked) {
      setSelectedCompanies([...selectedCompanies, companyId]);
    } else {
      setSelectedCompanies(selectedCompanies.filter(id => id !== companyId));
    }
  };

  /**
   * Handles the select all checkbox change event.
   * If the checkbox is checked, it sets the selectedCompanies state to include all company ids.
   * If the checkbox is unchecked, it clears the selectedCompanies state.
   * @param {object} event - The select all checkbox change event.
   */

  const handleSelectAllChange = (event) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedCompanies(companyList?.map(company => company.id));
    } else {
      setSelectedCompanies([]);
    }
  };

  const onSubmit = handleSubmit(async (formData) => {

    try {
      const response = await getCompanyList(formData);
      if (response?.success && response.data != null) {
        setCompanyList(response.data);
      } else {
        console.error(`Failed to fetch company list. Status: ${response.status}`);
        setError(`Failed to fetch company list. Error fetching data: ${error.message}`);
      }
    } catch (error) {
      console.error('Error fetching Companys:', error);
      enqueueSnackbar('Failed to fetch Companys', { variant: 'error' });
    }
  });

  /**
   * Fetches the list of industries from the server and sets the state with the received data.
   * @returns {Promise<void>}
   * @throws {Error} if unable to fetch the industry list
   */
  const fetchIndustryList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.industry}`);
      const data = await response.json();
      setIndustryList(data || []);
    } catch (error) {
      console.error("Failed to fetch industry list", error);
    }
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Slice data for pagination
  const paginatedCompany = companyList?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCopy = (e, CompanyId) => {
    e.stopPropagation(); // Prevent row click action
    navigator.clipboard.writeText(CompanyId || '');
    enqueueSnackbar('Copied to clipboard', { variant: 'success' });
  };

  if (loading || !industryList || !industryList?.length > 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '60px', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  };

  return (
    <FormProvider {...methods}>
      <Container style={{ marginTop: '80px', marginRight: '20px' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>
            Company List
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Button variant="contained" onClick={handleSearch}>
            {searchParameter ? "close search" : "search company"}
          </Button>
          <Box>
            <Button variant="contained"
              onClick={downloadSelectedCompaniesData}
              disabled={selectedCompanies.length === 0}
            >Download List</Button>

            <Button variant="contained" sx={{ marginLeft: 3 }} onClick={handleAddCompany}>
              Add New Company
            </Button>
          </Box>
        </Stack>
        <Grid container spacing={2}>
          {searchParameter && <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ padding: '1rem' }}>
              <Typography variant="h6" sx={{ marginBottom: '0.5rem' }}>Search Parameters</Typography>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={2}>
                  <Button type="submit" variant="contained" disabled={isSubmitting}> <SearchIcon /></Button>
                  <Controller
                    control={control}
                    name="companyName"
                    render={({ field }) =>
                      <TypeAhead
                        {...field}
                        api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.company}`}
                        multiple={false}
                        fullWidth
                        label="Company Name"
                        displayTextKey="name"
                        freeSolo={true}
                        onChange={(e, value) => field.onChange(value)}
                        onInputChange={(e, value) => field.onChange(value)}
                      />
                    }
                  />
                  <Controller
                    control={control}
                    name="industrySector"
                    render={({ field }) => (
                      <FormControl fullWidth >
                        <InputLabel size='small' id="type-label">Industry Sector Name</InputLabel>
                        <Select
                          {...field}
                          size="small"
                          label="Industry Sector Name"
                          displayTextKey="name"
                          variant="outlined"
                          fullWidth
                          placeholder="Industry Sector"
                          onChange={(e) => {
                            field.onChange(e);
                          }}
                        >
                          {industryList.map((item) => (
                            <MenuItem key={item._id} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={control}
                    name="types"
                    render={({ field }) => (
                      <FormControl fullWidth >
                        <InputLabel size='small' id="type-label">Types of company</InputLabel>
                        <Select labelId="type-label" label="Company Type"{...field} size="small">
                          <MenuItem value="" disabled>Select Company Type</MenuItem>
                          {company_types.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={control}
                    name="size"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size='small'
                        label=" Approx. Revenue (Cr)"
                        variant="outlined"
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="employees"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size='small'
                        label="Approx. Employee Headcount"
                        variant="outlined"
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="location"
                    render={({ field }) => (
                      <TypeAhead
                        {...field}
                        api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.location}`}
                        label="Location"
                        placeholder="Select locations"
                        displayTextKey='name'
                        multiple={true}
                        fullWidth
                        onChange={(e, value) => field.onChange(value)}
                      />
                    )}
                  />
                </Stack>
              </form>
            </Paper>
          </Grid>}
          <Grid item xs={12} md={searchParameter ? 8 : 12}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper elevation={3}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
                  <Table stickyHeader >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Checkbox
                            checked={selectAll}
                            onChange={handleSelectAllChange}
                          />
                        </TableCell>
                        <TableCell>#</TableCell>
                        <TableCell>Id</TableCell>
                        {/* <TableCell>company master id</TableCell> */}
                        <TableCell>Full Name</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Industry Sector</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Phone Number </TableCell>
                        <TableCell>Email</TableCell>
                        {/* <TableCell>Status</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCompany?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7}>No companies available.</TableCell>
                        </TableRow>
                      ) : (
                        paginatedCompany?.map((company, index) => (
                          <TableRow key={company.id} onClick={() => handleRowClick(company.id)} sx={{ cursor: 'pointer' }}>
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              <Checkbox
                                checked={selectedCompanies?.includes(company.id)}
                                onChange={(event) => handleCheckboxChange(event, company.id)}
                              />
                            </TableCell>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>
                              {/* {company?.id} */}
                              <IconButton
                                size="small"
                                onClick={(e) => handleCopy(e, company?.id)}
                                style={{ marginLeft: 8 }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                            {/* <TableCell>{company?.company_master_id}</TableCell> */}
                            <TableCell>{company?.fullName}</TableCell>
                            <TableCell>{company?.location}</TableCell>
                            <TableCell>{company?.industrySector}</TableCell>
                            <TableCell>{company?.type}</TableCell>
                            <TableCell>{company?.PhoneNumber}</TableCell>
                            <TableCell>{company?.email}</TableCell>
                            {/* <TableCell>{company?.source}</TableCell> */}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Component */}
                <TablePagination
                  component="div"
                  count={companyList?.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[20]}
                />
              </Paper>)}
          </Grid>
        </Grid>
      </Container>
    </FormProvider>
  );
};

export default CompanyList;
